/* eslint-disable indent */
import { IRelease } from '@/types'
import { saveRelease, unsaveRelease } from 'api/releases'
import classNames from 'classnames'
import { Icon } from 'components/common/Icon'
import { ToastErrorIcon } from 'components/common/Toast'
import { ReleaseInfoTag } from 'components/release-calendar/ReleaseInfoTag'
import { SavedReleaseModal } from 'components/release-calendar/SavedReleaseModal'
import {
  useAuth,
  useAuthModal,
  useOverlay,
  useSavedReleasesIds,
} from 'contexts'
import dayjs from 'dayjs'
import { useUserProfile } from 'hooks'
import { useReleaseInfo } from 'hooks/useReleaseInfo'
import useTranslation from 'next-translate/useTranslation'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Fragment, MouseEvent, useCallback, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { PropsWithClassName } from 'types'
import { flatCollectionType } from 'utils/data'
import { getImageUrl } from 'utils/image'
import { getExactReleaseDate } from 'utils/release'
import { getArtistUrl, getReleaseUrl } from 'utils/urls'

export type ReleaseCardProps = {
  id: number
  keepAspectRatio?: boolean
  priority?: boolean
} & IRelease['attributes'] &
  PropsWithClassName

export const ReleaseCard = (props: ReleaseCardProps) => {
  const {
    className,
    id,
    name,
    artists,
    artworkImages,
    extraArtists,
    keepAspectRatio,
    priority,
  } = props

  const router = useRouter()
  const { user } = useAuth()
  const { setIsOpen, setState } = useAuthModal()

  const artworkImage =
    artworkImages?.data?.find(image =>
      image.attributes.name?.includes('(1)')
    ) || artworkImages?.data?.[0]

  if (!id || !name) return null

  const href = user
    ? getReleaseUrl(
        id,
        name,
        artists?.data?.map(artist => ({
          id: artist.id,
          name: artist.attributes.name || '',
        })) || [],
        extraArtists
      )
    : '#'

  const onCardClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()

    if (!user) {
      setState('register')
      setIsOpen(true)
      return
    }

    router.push(
      { pathname: href, query: { tab: router.query.tab } },
      undefined,
      {
        scroll: false,
      }
    )
  }

  return (
    <div className={classNames(className, 'relative')}>
      <Link href={href} onClick={onCardClick}>
        <div
          className={classNames(
            'group relative w-full overflow-hidden rounded bg-cool-50',
            keepAspectRatio
              ? 'aspect-1'
              : 'aspect-4/3 [@media(min-width:369px)]:aspect-1'
          )}
        >
          {artworkImage?.attributes?.url && (
            <div className='relative h-full w-full'>
              <div
                className={
                  'absolute left-0 top-0 z-[1] h-full w-full rounded bg-black bg-opacity-[15%]'
                }
              />

              <Image
                src={
                  getImageUrl(flatCollectionType(artworkImage), 'small') ||
                  artworkImage.attributes.url
                }
                alt={name || ''}
                fill
                className='rounded object-cover transition-transform duration-300 ease-in-out group-hover:scale-[1.15]'
                quality={75}
                sizes='20vw'
                priority={priority}
                loading={priority ? 'eager' : 'lazy'}
              />
            </div>
          )}

          <ReleaseCardCountdownInfo {...props} />

          <TimedEdition {...props} />
        </div>

        <h5 className='mt-2.5 overflow-hidden overflow-ellipsis whitespace-nowrap font-body text-sm font-medium tracking-normal'>
          {name}
        </h5>
      </Link>

      {(Boolean(artists?.data?.length) || extraArtists) && (
        <div className='overflow-hidden overflow-ellipsis whitespace-nowrap'>
          {artists?.data?.map(
            (artist, index) =>
              artist.attributes?.name && (
                <Fragment key={artist.id}>
                  <Link
                    href={getArtistUrl(artist.attributes.name, artist.id)}
                    target='_blank'
                    className='inline w-max items-center space-x-1.5 text-sm text-cool-600 hover:text-secondary-default'
                  >
                    {artist?.attributes.name}
                  </Link>

                  {artists?.data?.[index + 1] && (
                    <span className='mx-1 text-sm text-cool-600'>X</span>
                  )}
                </Fragment>
              )
          )}

          {extraArtists && (
            <span className='text-sm text-cool-600'>
              {Boolean(artists?.data?.length) && ' X '}
              {extraArtists}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

const ReleaseCardCountdownInfo = ({
  id,
  name,
  artworkImages,
  releaseDate,
  raffle,
}: ReleaseCardProps) => {
  const { t } = useTranslation('release_calendar')
  const { user } = useAuth()
  const { setConfig, setIsOpen } = useOverlay()
  const { setState: setAuthState, setIsOpen: setAuthIsOpen } = useAuthModal()
  const queryClient = useQueryClient()
  const { data: savedIds } = useSavedReleasesIds()
  const releaseInfo = useReleaseInfo(releaseDate, raffle)

  const isSaved = savedIds?.includes(id)

  const refetchSavedReleases = () => {
    queryClient.refetchQueries({ queryKey: ['releases', 'saved'] })
  }

  const onSaveRelease = async () => {
    const image = artworkImages?.data?.[0]

    const { error } = await saveRelease(id)

    if (error) {
      toast.error(error.message, { icon: <ToastErrorIcon /> })
      return
    }

    setConfig({
      className: 'flex items-center px-4',
      body: (
        <SavedReleaseModal
          imageAlt={name}
          imageSrc={
            image
              ? getImageUrl(flatCollectionType(image), 'thumbnail')
              : undefined
          }
        />
      ),
    })
    setIsOpen(true)
    refetchSavedReleases()
  }

  const onUnsaveRelease = async () => {
    const { error } = await unsaveRelease(id)

    if (error) {
      toast.error(error.message, { icon: <ToastErrorIcon /> })
      return
    }

    refetchSavedReleases()
  }

  const onSaveButtonClick = async () => {
    if (!user) {
      setAuthState('register')
      setAuthIsOpen(true)
      return
    }

    if (isSaved) {
      await onUnsaveRelease()
    } else {
      await onSaveRelease()
    }
  }

  const { canSaveRelease } = releaseInfo

  return (
    <>
      <ReleaseInfoTag
        className='absolute left-3 top-3 z-[1]'
        releaseDate={releaseDate}
        raffle={raffle}
      />

      {canSaveRelease && (
        <button
          type='button'
          role='checkbox'
          className={classNames(
            'group absolute right-3 top-3 z-[2] h-max w-max rounded-full p-3 shadow-card  transition-colors duration-200 ease-in-out',
            isSaved ? 'bg-primary-default' : 'bg-cool-50'
          )}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            onSaveButtonClick()
          }}
          aria-checked={Boolean(isSaved)}
        >
          <span className='sr-only'>{t('get_notified_about_release')}</span>

          <Icon
            size={16}
            name={isSaved ? 'filled-bell' : 'bell'}
            filled={isSaved}
            className={classNames(
              isSaved
                ? 'text-cool-50'
                : 'text-cool-600 transition-colors group-hover:text-purple-500'
            )}
          />
        </button>
      )}
    </>
  )
}

const TimedEdition = ({ releaseDate, raffle }: ReleaseCardProps) => {
  const { t } = useTranslation('release_calendar')
  const { data: userProfile } = useUserProfile()

  const getText = useCallback(() => {
    if (!releaseDate || !releaseDate.timedEdition) return null

    const timezone = userProfile?.releaseSettings?.timeZone || dayjs.tz.guess()
    const { releaseStart, releaseEnd } = getExactReleaseDate(
      releaseDate,
      timezone,
      raffle
    )

    if (!releaseStart || !releaseEnd) return null

    const hoursDiff = dayjs(releaseEnd).diff(dayjs(releaseStart), 'hours')

    if (isNaN(hoursDiff)) return null

    if (hoursDiff <= 72) {
      return t(raffle ? 'common:h_raffle' : 'common:h_timed_edition', {
        h: hoursDiff,
      })
    }

    return t(raffle ? 'common:d_raffle' : 'common:d_timed_edition', {
      d: Math.floor(hoursDiff / 24),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseDate, raffle])

  const [text, setText] = useState(getText)

  useEffect(() => {
    const timeout = setTimeout(() => setText(getText()), 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [text, getText])

  if (!releaseDate?.timedEdition || !text) return null

  return (
    <div className='absolute bottom-3 left-3 z-[2] h-max w-max rounded-full bg-secondary-default px-2 py-1 text-2xs text-cool-50'>
      {text}
    </div>
  )
}
